/**
 * Dates on tasks — US 4.2.
 *
 * A date with no time is a calendar day and reads the same everywhere (no
 * time zone can move it). A date with a time is wall-clock time in the zone
 * of whoever set it, shown in each viewer's own zone — so it may land on a
 * different day for someone else, but always the same day for one person on
 * any device. Sorting always uses the real value, never the label.
 */
import type { RepeatRule } from "./api";

export const iso = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export function parseIso(value: string): Date {
  const [y, m, d] = value.split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

export const addDays = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const dayDiff = (a: Date, b: Date) => Math.round((parseIso(iso(a)).getTime() - parseIso(iso(b)).getTime()) / 86_400_000);

function zoneOffsetMs(ts: number, tz: string): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(new Date(ts));
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value);
  return Date.UTC(get("year"), get("month") - 1, get("day"), get("hour") % 24, get("minute"), get("second")) - ts;
}

/** The instant a wall-clock date + time in `tz` stands for. */
export function zonedInstant(date: string, time: string, tz: string): Date {
  const [y, m, d] = date.split("-").map(Number);
  const [hh, mm] = time.split(":").map(Number);
  const guess = Date.UTC(y, m - 1, d, hh, mm);
  if (!tz) return new Date(y, m - 1, d, hh, mm);
  try {
    const first = guess - zoneOffsetMs(guess, tz);
    return new Date(guess - zoneOffsetMs(first, tz));
  } catch {
    return new Date(y, m - 1, d, hh, mm);
  }
}

/** A task's date (+ optional time in `tz`) as this viewer sees it. */
export function viewerDateTime(date: string | null, time: string | null, tz: string): { date: Date; time: string | null } | null {
  if (!date) return null;
  if (!time) return { date: parseIso(date), time: null };
  const at = zonedInstant(date, time, tz);
  return {
    date: new Date(at.getFullYear(), at.getMonth(), at.getDate()),
    time: `${String(at.getHours()).padStart(2, "0")}:${String(at.getMinutes()).padStart(2, "0")}`,
  };
}

export type DueTone = "overdue" | "soon" | "normal";

/**
 * The words a date shows as: "Today", "Tomorrow", "Fri" within a week,
 * "24 Oct" beyond — plus the time when there is one. Overdue reads red,
 * today and tomorrow amber (US 4.2).
 */
export function formatTaskDate(
  date: string | null,
  time: string | null,
  tz: string,
  t: (k: string, o?: Record<string, unknown>) => string,
  locale: string,
  { done = false, now = new Date() }: { done?: boolean; now?: Date } = {},
): { label: string; tone: DueTone; title: string } | null {
  const v = viewerDateTime(date, time, tz);
  if (!v) return null;
  const diff = dayDiff(v.date, now);
  let label: string;
  if (diff === 0) label = t("tasks.dates.today", { defaultValue: "Today" });
  else if (diff === 1) label = t("tasks.dates.tomorrow", { defaultValue: "Tomorrow" });
  else if (diff === -1) label = t("tasks.dates.yesterday", { defaultValue: "Yesterday" });
  else if (Math.abs(diff) < 7) label = v.date.toLocaleDateString(locale, { weekday: "short" });
  else
    label = v.date.toLocaleDateString(locale, {
      day: "numeric",
      month: "short",
      ...(v.date.getFullYear() !== now.getFullYear() ? { year: "numeric" } : {}),
    });
  if (v.time) label = `${label}, ${v.time}`;
  let tone: DueTone = "normal";
  if (!done) {
    const past = diff < 0 || (diff === 0 && v.time !== null && zonedInstant(date!, time!, tz) < now);
    if (past) tone = "overdue";
    else if (diff === 0 || diff === 1) tone = "soon";
  }
  const title = v.date.toLocaleDateString(locale, { weekday: "long", day: "numeric", month: "long", year: "numeric" }) + (v.time ? ` ${v.time}` : "");
  return { label, tone, title };
}

export const DUE_TONE_CLASS: Record<DueTone, string> = {
  overdue: "text-red-600 dark:text-red-400",
  soon: "text-amber-600 dark:text-amber-400",
  normal: "text-muted-foreground",
};

/** Sort key for a due date that never compares labels (US 4.2 failure criterion). */
export function dueSortKey(date: string | null, time: string | null, tz: string): number {
  if (!date) return Number.POSITIVE_INFINITY;
  return time ? zonedInstant(date, time, tz).getTime() : parseIso(date).getTime() + 86_399_000;
}

const WEEKDAYS_EN = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
const WEEKDAYS_AR = ["الأحد", "الاثنين", "الثلاثاء", "الأربعاء", "الخميس", "الجمعة", "السبت"];

/**
 * Turn what someone typed into a date: "24/10", "24/10/2026", "2026-10-24",
 * "today", "tomorrow", "fri", "next thu", "in 3 days" (and the Arabic
 * today / tomorrow / weekday names). Null when it isn't a date.
 */
export function parseTypedDate(input: string, now = new Date()): string | null {
  const s = input.trim().toLowerCase().replace(/\s+/g, " ");
  if (!s) return null;
  if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(s)) {
    const d = parseIso(s);
    return Number.isNaN(d.getTime()) ? null : iso(d);
  }
  const dm = /^(\d{1,2})[/.-](\d{1,2})(?:[/.-](\d{2,4}))?$/.exec(s);
  if (dm) {
    const day = Number(dm[1]);
    const month = Number(dm[2]);
    let year = dm[3] ? Number(dm[3]) : now.getFullYear();
    if (year < 100) year += 2000;
    if (month < 1 || month > 12 || day < 1 || day > 31) return null;
    const d = new Date(year, month - 1, day);
    if (d.getMonth() !== month - 1) return null;
    if (!dm[3] && dayDiff(d, now) < -1) d.setFullYear(year + 1);
    return iso(d);
  }
  if (["today", "اليوم", "النهارده", "النهاردة"].includes(s)) return iso(now);
  if (["tomorrow", "tmr", "غدا", "غداً", "بكرة", "بكره"].includes(s)) return iso(addDays(now, 1));
  if (s === "yesterday" || s === "أمس" || s === "امبارح") return iso(addDays(now, -1));
  const inDays = /^in (\d{1,3}) (day|days|week|weeks)$/.exec(s);
  if (inDays) return iso(addDays(now, Number(inDays[1]) * (inDays[2].startsWith("week") ? 7 : 1)));
  if (s === "next week") return iso(addDays(now, 7));
  const wd = /^(next )?([a-z]+|[؀-ۿ]+)$/.exec(s);
  if (wd) {
    const word = wd[2];
    let target = WEEKDAYS_EN.findIndex((d) => word.startsWith(d) && word.length >= 3);
    if (target < 0) target = WEEKDAYS_AR.findIndex((d) => d === word || d === `ال${word}`);
    if (target >= 0) {
      // "thu" and "next thu" both mean the coming Thursday.
      let ahead = (target - now.getDay() + 7) % 7;
      if (ahead === 0) ahead = 7;
      return iso(addDays(now, ahead));
    }
  }
  return null;
}

/** "next week" for the shortcut: the coming week's first day (US 4.2). */
export function nextWeekStart(weekStart: number, now = new Date()): string {
  let ahead = (weekStart - now.getDay() + 7) % 7;
  if (ahead === 0) ahead = 7;
  return iso(addDays(now, ahead));
}

/** Six rows of a month, starting on the Workspace's first day of the week. */
export function monthCells(year: number, month: number, weekStart: number): Date[] {
  const first = new Date(year, month, 1);
  const lead = (first.getDay() - weekStart + 7) % 7;
  return Array.from({ length: 42 }, (_, i) => new Date(year, month, 1 - lead + i));
}

// ── Repeat (US 4.10) — the same rules the server applies ──────────────────

const lastDay = (y: number, m: number) => new Date(y, m + 1, 0).getDate();

function nthWeekday(year: number, month: number, nth: number, weekday: number): Date {
  const matches: Date[] = [];
  for (let d = 1; d <= lastDay(year, month); d++) {
    const date = new Date(year, month, d);
    if (date.getDay() === weekday) matches.push(date);
  }
  return nth < 0 ? matches[matches.length - 1] : matches[Math.min(nth, matches.length) - 1];
}

export function nextOccurrence(rule: RepeatRule, after: Date, workweek: "sun_thu" | "mon_fri"): Date | null {
  switch (rule.kind) {
    case "daily":
      return addDays(after, 1);
    case "workday": {
      const work = workweek === "sun_thu" ? [0, 1, 2, 3, 4] : [1, 2, 3, 4, 5];
      let d = addDays(after, 1);
      while (!work.includes(d.getDay())) d = addDays(d, 1);
      return d;
    }
    case "weekly": {
      const days = rule.days?.length ? rule.days : [after.getDay()];
      let d = addDays(after, 1);
      for (let i = 0; i < 7; i++, d = addDays(d, 1)) if (days.includes(d.getDay())) return d;
      return null;
    }
    case "monthly": {
      if (rule.mode === "pattern") {
        const here = nthWeekday(after.getFullYear(), after.getMonth(), rule.nth ?? 1, rule.weekday ?? 0);
        if (here > after) return here;
        const nm = new Date(after.getFullYear(), after.getMonth() + 1, 1);
        return nthWeekday(nm.getFullYear(), nm.getMonth(), rule.nth ?? 1, rule.weekday ?? 0);
      }
      const nm = new Date(after.getFullYear(), after.getMonth() + 1, 1);
      const day = rule.day ?? after.getDate();
      return new Date(nm.getFullYear(), nm.getMonth(), Math.min(day, lastDay(nm.getFullYear(), nm.getMonth())));
    }
    case "yearly": {
      const y = after.getFullYear() + 1;
      return new Date(y, after.getMonth(), Math.min(after.getDate(), lastDay(y, after.getMonth())));
    }
    case "custom": {
      const n = Math.max(1, rule.interval ?? 1);
      if (rule.unit === "week") return addDays(after, 7 * n);
      if (rule.unit === "month") {
        const target = new Date(after.getFullYear(), after.getMonth() + n, 1);
        return new Date(target.getFullYear(), target.getMonth(), Math.min(after.getDate(), lastDay(target.getFullYear(), target.getMonth())));
      }
      return addDays(after, n);
    }
    default:
      return null;
  }
}

/** The next `count` dates a rule falls on after `from` — the preview. */
export function previewDates(rule: RepeatRule, from: Date, workweek: "sun_thu" | "mon_fri", count = 3): Date[] {
  const out: Date[] = [];
  let cur = from;
  for (let i = 0; i < count; i++) {
    const next = nextOccurrence(rule, cur, workweek);
    if (!next) break;
    out.push(next);
    cur = next;
  }
  return out;
}
